import { ERROR_CODES, RETRYABLE_CODES, type ApiErrorBody, type ErrorCode } from './common.ts';

/**
 * Traducción común (Edge Functions y transporte RPC) de resultados de las
 * funciones SQL al contrato. Un resultado confirmado con status "conflict" se
 * expone como error REVISION_CONFLICT (409) conservando los detalles: la
 * propuesta ya quedó guardada como rama.
 */
export function rpcResultToError(data: unknown): ApiErrorBody | null {
  if (data && typeof data === 'object' && (data as { status?: unknown }).status === 'conflict') {
    return {
      code: 'REVISION_CONFLICT',
      message: 'Otro equipo guardó antes. Tu versión quedó guardada como propuesta; compara y elige.',
      retryable: false,
      details: data as Record<string, unknown>,
    };
  }
  return null;
}

export interface PostgrestLikeError {
  message?: string;
  details?: string | null;
  hint?: string | null;
  code?: string;
}

const KNOWN = new Set<string>(ERROR_CODES);

export function errorFromPostgrest(err: PostgrestLikeError): ApiErrorBody {
  const message = err.message ?? '';
  if (KNOWN.has(message)) {
    const code = message as ErrorCode;
    let details: Record<string, unknown> | undefined;
    if (err.hint) {
      try {
        const parsed = JSON.parse(err.hint) as unknown;
        if (parsed && typeof parsed === 'object' && Object.keys(parsed).length) details = parsed as Record<string, unknown>;
      } catch {
        details = undefined;
      }
    }
    return { code, message: err.details || code, retryable: RETRYABLE_CODES.has(code), ...(details ? { details } : {}) };
  }
  if (message === 'IMMUTABLE_REVISION') {
    return { code: 'FORBIDDEN', message: 'Las revisiones son inmutables.', retryable: false };
  }
  if (err.code === '42501') return { code: 'FORBIDDEN', message: 'Operación no permitida para esta credencial.', retryable: false };
  if (err.code === 'PGRST301' || err.code === 'PGRST303' || err.code === 'PGRST302') {
    return { code: 'AUTH_REQUIRED', message: 'Sesión inválida o vencida.', retryable: false };
  }
  if (err.code === '40001' || err.code === '40P01' || err.code === '57014') {
    return { code: 'INTERNAL', message: 'Conflicto temporal en la base de datos; reintenta.', retryable: true };
  }
  if (err.code === '23505') return { code: 'VALIDATION_FAILED', message: 'Ya existe un registro con esos datos.', retryable: false };
  if (err.code === '22P02' || err.code === '23514' || err.code === '23502' || err.code === '22023') {
    return { code: 'VALIDATION_FAILED', message: 'Datos inválidos.', retryable: false };
  }
  return { code: 'INTERNAL', message: 'Error interno.', retryable: true };
}

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly retryable: boolean;
  readonly details?: Record<string, unknown>;
  readonly requestId?: string;
  constructor(body: ApiErrorBody, requestId?: string) {
    super(body.message);
    this.name = 'ApiError';
    this.code = body.code;
    this.retryable = body.retryable;
    if (body.details) this.details = body.details;
    if (requestId) this.requestId = requestId;
  }
}
